import { useState, useEffect } from 'react'
import { Sparkles, FileText, Wand2, Copy, Download, RefreshCw, Settings2, Clock, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/use-toast'
import { Progress } from '@/components/ui/progress'
import { FluentCard } from '@/components/layout/page-header'
import { aiService, type GenerationOptions, type RecentGeneration } from '@/lib/api/services/ai'

export default function AIStudio() {
  const { toast } = useToast()
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generatedContent, setGeneratedContent] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [recentGenerations, setRecentGenerations] = useState<RecentGeneration[]>([])
  const [options, setOptions] = useState<GenerationOptions>({
    tone: 'professional',
    length: 'medium',
    creativity: 50,
    includeImages: false,
    language: 'en',
  })

  const templates = [
    { id: 'blog', name: 'Blog Post', icon: FileText },
    { id: 'email', name: 'Email Campaign', icon: FileText },
    { id: 'presentation', name: 'Presentation', icon: FileText },
    { id: 'report', name: 'Business Report', icon: FileText },
    { id: 'social', name: 'Social Media', icon: FileText },
    { id: 'proposal', name: 'Project Proposal', icon: FileText },
  ]

  useEffect(() => {
    loadRecentGenerations()
  }, [])

  const loadRecentGenerations = async () => {
    try {
      const data = await aiService.getRecentGenerations()
      setRecentGenerations(data)
    } catch (error) {
      console.error('Failed to load recent generations:', error)
    }
  }

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a prompt',
        variant: 'destructive',
      })
      return
    }

    setGenerating(true)
    try {
      const response = await aiService.generateContent({
        prompt,
        templateType: selectedTemplate || undefined,
        options,
      })

      setGeneratedContent(response.content)
      toast({
        title: 'Success',
        description: 'Content generated successfully',
      })

      // Reload recent generations
      loadRecentGenerations()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to generate content',
        variant: 'destructive',
      })
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent)
    toast({
      title: 'Copied',
      description: 'Content copied to clipboard',
    })
  }

  const handleExport = () => {
    const blob = new Blob([generatedContent], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'generated-content.md'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    toast({
      title: 'Downloaded',
      description: 'Content downloaded successfully',
    })
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-none bg-gradient-to-r from-amber-500 to-orange-600 p-8 lg:p-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 flex items-center gap-4">
          <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-sm">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-1">AI Studio</h1>
            <p className="text-white/80">Generate content with AI-powered tools</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
                <Wand2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Content Generator</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Describe what you want to create and let AI do the work</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Select Template (Optional)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {templates.map((template) => {
                    const Icon = template.icon
                    return (
                      <button
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id === selectedTemplate ? '' : template.id)}
                        className={`flex items-center gap-2 p-3 rounded-xl text-sm font-medium transition-all ${
                          selectedTemplate === template.id
                            ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {template.name}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prompt" className="text-gray-700 dark:text-gray-300">Your Prompt</Label>
                <Textarea
                  id="prompt"
                  placeholder="Describe what you want to create..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  className="resize-none rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-12"
              >
                {generating ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Zap className="mr-2 h-4 w-4" />
                    Generate Content
                  </>
                )}
              </Button>

              {generating && (
                <div className="space-y-2 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20">
                  <Progress value={66} className="h-2" />
                  <p className="text-sm text-amber-700 dark:text-amber-300 text-center">
                    AI is crafting your content...
                  </p>
                </div>
              )}
            </div>
          </FluentCard>

          {generatedContent && (
            <FluentCard>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generated Content</h3>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopy} className="rounded-xl">
                    <Copy className="mr-2 h-4 w-4" />
                    Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExport} className="rounded-xl">
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{generatedContent}</pre>
              </div>
            </FluentCard>
          )}
        </div>

        <div className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-gray-400 to-gray-600">
                <Settings2 className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generation Options</h3>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Tone</Label>
                <select
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  value={options.tone}
                  onChange={(e) => setOptions({ ...options, tone: e.target.value })}
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="creative">Creative</option>
                  <option value="formal">Formal</option>
                  <option value="friendly">Friendly</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Length</Label>
                <select
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  value={options.length}
                  onChange={(e) => setOptions({ ...options, length: e.target.value })}
                >
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                  <option value="detailed">Very Detailed</option>
                </select>
              </div>

              <div className="space-y-3">
                <Label className="text-gray-700 dark:text-gray-300">Creativity Level</Label>
                <Slider
                  value={[options.creativity]}
                  onValueChange={(value) =>
                    setOptions({ ...options, creativity: value[0] ?? 50 })
                  }
                  max={100}
                  step={10}
                  className="py-2"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>Conservative</span>
                  <span className="font-medium text-amber-600 dark:text-amber-400">{options.creativity}%</span>
                  <span>Creative</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Include Images</Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Generate with AI images
                  </p>
                </div>
                <Switch
                  checked={options.includeImages}
                  onCheckedChange={(checked) =>
                    setOptions({ ...options, includeImages: checked })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-300">Language</Label>
                <select
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  value={options.language}
                  onChange={(e) => setOptions({ ...options, language: e.target.value })}
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="ja">Japanese</option>
                </select>
              </div>
            </div>
          </FluentCard>

          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Generations</h3>
            </div>

            <div className="space-y-3">
              {recentGenerations.length > 0 ? (
                recentGenerations.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{item.type}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-lg text-gray-600 dark:text-gray-400">
                      View
                    </Button>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <Sparkles className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No recent generations yet
                  </p>
                </div>
              )}
            </div>
          </FluentCard>
        </div>
      </div>
    </div>
  )
}
